import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, ShieldCheck, Users, Check, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

// ── Matriz de permisos por módulo ─────────────────────────────────────────
function PermisosMatrix({ permisos, seleccionados, onChange, disabled }) {
  const [expandidos, setExpandidos] = useState({});

  // Agrupar por módulo
  const modulos = permisos.reduce((acc, p) => {
    if (!acc[p.modulo]) acc[p.modulo] = [];
    acc[p.modulo].push(p);
    return acc;
  }, {});

  const toggleModulo = (mod) => setExpandidos(e => ({ ...e, [mod]: !e[mod] }));

  const togglePermiso = (id) => {
    if (disabled) return;
    onChange(seleccionados.includes(id)
      ? seleccionados.filter(x => x !== id)
      : [...seleccionados, id]
    );
  };

  const toggleTodoModulo = (mod) => {
    if (disabled) return;
    const ids = modulos[mod].map(p => p.id);
    const todosActivos = ids.every(id => seleccionados.includes(id));
    if (todosActivos) {
      onChange(seleccionados.filter(id => !ids.includes(id)));
    } else {
      onChange([...new Set([...seleccionados, ...ids])]);
    }
  };

  return (
    <div className="space-y-2">
      {Object.entries(modulos).map(([mod, perms]) => {
        const ids         = perms.map(p => p.id);
        const activos     = ids.filter(id => seleccionados.includes(id)).length;
        const todosActivos = activos === ids.length;
        const expandido   = expandidos[mod] ?? true;

        return (
          <div key={mod} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            {/* Header del módulo */}
            <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50">
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggleTodoModulo(mod)}
                  className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-colors shrink-0 ${
                    todosActivos
                      ? 'bg-primary-500 border-primary-500'
                      : activos > 0
                        ? 'bg-primary-200 border-primary-400 dark:bg-primary-500/30'
                        : 'border-slate-300 dark:border-slate-600'
                  }`}
                >
                  {(todosActivos || activos > 0) && <Check size={10} className="text-white" />}
                </button>
              )}
              <span className="flex-1 text-[12px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide">
                {mod}
              </span>
              <span className="text-[11px] text-slate-400">{activos}/{ids.length}</span>
              <button
                type="button"
                onClick={() => toggleModulo(mod)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                {expandido ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>

            {/* Permisos del módulo */}
            {expandido && (
              <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {perms.map(p => {
                  const activo = seleccionados.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center gap-3 px-4 py-2 transition-colors
                        ${disabled ? 'cursor-default' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/30'}`}
                    >
                      <div
                        onClick={() => togglePermiso(p.id)}
                        className={`w-4 h-4 rounded flex items-center justify-center border-2 transition-colors shrink-0 ${
                          activo
                            ? 'bg-primary-500 border-primary-500'
                            : 'border-slate-300 dark:border-slate-600'
                        }`}
                      >
                        {activo && <Check size={10} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-slate-700 dark:text-slate-300">{p.descripcion}</p>
                        <p className="text-[10px] font-mono text-slate-400">{p.clave}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Modal crear / editar rol ──────────────────────────────────────────────
function RolModal({ isOpen, onClose, rol, permisos, onSave }) {
  const [nombre,      setNombre]      = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [permisoIds,  setPermisoIds]  = useState([]);
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNombre(rol?.nombre ?? '');
      setDescripcion(rol?.descripcion ?? '');
      setPermisoIds(rol?.permiso_ids ?? []);
    }
  }, [isOpen, rol]);

  const handleSave = async () => {
    if (!nombre.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    await onSave({ nombre: nombre.trim(), descripcion: descripcion.trim(), permiso_ids: permisoIds });
    setSaving(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={rol ? `Editar rol: ${rol.nombre}` : 'Nuevo rol'}
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : rol ? 'Guardar cambios' : 'Crear rol'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label>Nombre *</label>
            <input
              className="input"
              placeholder="Ej: Cajero"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label>Descripción</label>
            <input
              className="input"
              placeholder="Breve descripción del rol"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="mb-0">Permisos</label>
            <div className="flex gap-2">
              <button
                type="button"
                className="text-[11px] text-primary-500 hover:underline"
                onClick={() => setPermisoIds(permisos.map(p => p.id))}
              >
                Seleccionar todos
              </button>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <button
                type="button"
                className="text-[11px] text-slate-400 hover:underline"
                onClick={() => setPermisoIds([])}
              >
                Ninguno
              </button>
            </div>
          </div>
          <div className="max-h-[400px] overflow-y-auto pr-0.5">
            <PermisosMatrix
              permisos={permisos}
              seleccionados={permisoIds}
              onChange={setPermisoIds}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Card de rol ───────────────────────────────────────────────────────────
function RolCard({ rol, permisos, onEdit, onDelete, canEdit }) {
  const [expandido, setExpandido] = useState(false);

  const modulos = [...new Set(
    permisos.filter(p => rol.permiso_ids.includes(p.id)).map(p => p.modulo)
  )];

  return (
    <div className="card space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center shrink-0">
            <ShieldCheck size={16} className="text-primary-500 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-slate-900 dark:text-white">{rol.nombre}</h3>
            {rol.descripcion && (
              <p className="text-[11px] text-slate-400 mt-0.5">{rol.descripcion}</p>
            )}
          </div>
        </div>

        {canEdit && (
          <div className="flex gap-0.5 shrink-0">
            <button
              onClick={() => onEdit(rol)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => onDelete(rol)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2.5 flex items-center gap-2">
          <Users size={13} className="text-slate-400 shrink-0" />
          <div>
            <p className="text-[11px] text-slate-400">Usuarios</p>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{rol.total_usuarios}</p>
          </div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2.5 flex items-center gap-2">
          <Check size={13} className="text-slate-400 shrink-0" />
          <div>
            <p className="text-[11px] text-slate-400">Permisos</p>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{rol.permiso_ids.length}</p>
          </div>
        </div>
      </div>

      {/* Módulos con acceso */}
      <div>
        <button
          onClick={() => setExpandido(e => !e)}
          className="w-full flex items-center justify-between text-[11px] font-semibold text-slate-400 uppercase tracking-wide hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <span>Módulos con acceso</span>
          {expandido ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>

        {expandido && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {modulos.length === 0 ? (
              <span className="text-xs text-slate-400">Sin permisos asignados</span>
            ) : modulos.map(m => (
              <span key={m} className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                {m}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page principal ────────────────────────────────────────────────────────
export default function Roles() {
  const { can } = useAuth();
  const canEdit = can('roles.gestionar');

  const [roles,    setRoles]    = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRol,   setEditRol]   = useState(null);
  const [confirmRol, setConfirmRol] = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/roles/index.php');
      setRoles(res.data.roles);
      setPermisos(res.data.permisos);
    } catch { toast.error('Error al cargar roles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async (data) => {
    try {
      if (editRol) {
        await api.put(`/roles/index.php?id=${editRol.id}`, data);
        toast.success('Rol actualizado');
      } else {
        await api.post('/roles/index.php', data);
        toast.success('Rol creado');
      }
      setModalOpen(false);
      setEditRol(null);
      fetch();
    } catch (err) { toast.error(err?.message || 'Error al guardar'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/roles/index.php?id=${confirmRol.id}`);
      toast.success('Rol eliminado');
      setConfirmRol(null);
      fetch();
    } catch (err) { toast.error(err?.message || 'Error al eliminar'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Roles y permisos</h1>
        {canEdit && (
          <button className="btn-primary" onClick={() => { setEditRol(null); setModalOpen(true); }}>
            <Plus size={16} /> Nuevo rol
          </button>
        )}
      </div>

      {loading ? (
        <div className="card flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {roles.map(rol => (
            <RolCard
              key={rol.id}
              rol={rol}
              permisos={permisos}
              canEdit={canEdit}
              onEdit={(r) => { setEditRol(r); setModalOpen(true); }}
              onDelete={(r) => setConfirmRol(r)}
            />
          ))}
          {roles.length === 0 && (
            <div className="col-span-full card flex flex-col items-center justify-center py-16 text-slate-300 dark:text-slate-600">
              <ShieldCheck size={48} strokeWidth={1.5} />
              <p className="text-sm mt-3 text-slate-400">Sin roles configurados</p>
            </div>
          )}
        </div>
      )}

      <RolModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditRol(null); }}
        rol={editRol}
        permisos={permisos}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={!!confirmRol}
        onClose={() => setConfirmRol(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Eliminar rol"
        message={`¿Eliminar el rol "${confirmRol?.nombre}"? Solo es posible si no hay usuarios asignados.`}
      />
    </div>
  );
}
